/* eslint-disable max-lines-per-function */
import * as React from "react";
import * as yup from "yup";
import { Button, Grid } from "@material-ui/core";
import { Form, Formik } from "formik";
import { useMutation } from "@apollo/client";
import { pick } from "lodash";
import {
  AddUserProfileAddress,
  UserProfile_userProfile_addresses as Address,
  EditUserProfileAddress,
} from "types/gql-types";
import { Modal } from "components/haven-design";
import { DialogContent, DialogActions } from "components/haven-design/modal/dialog";
// eslint-disable-next-line max-len
// import { NullableAddress, ValidationResult, getAddress, validateStreetAddress } from "services/validate-street-address";
import AddressGroup from "pages/dashboard/settings/property/buildings/address";
import { addUserProfileAddressMutation, editUserProfileAddressMutation } from "pages/clients/settings/queries";
import { useSnackbar } from "components/haven-design/snackbar";

type AddProps = {
  isOpen:boolean;
  setIsOpen: (value:boolean) => void;
};

type EditProps = {
  address:Address;
} & AddProps;

const schema = yup.object().shape({
  address1: yup.string().required(),
  city: yup.string().required(),
  state: yup.string().required(),
  zip: yup.string().required()
    .trim()
    .matches(/^\d{5}(?:[-\s]\d{4})?$/, "Incorrect zip"),
});

const ProfileAddressesBaseModal:React.FC<AddProps & {
  mode: "Edit" | "Add",
  initialValues:Address,
  onSubmit:(values:Address) => void,
  loading:boolean,
}> = ({
  mode,
  isOpen,
  setIsOpen,
  initialValues,
  onSubmit,
  loading,
}) => (<>
  <Modal fullScreen isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <>
          <DialogContent>
            <Form id="edit-address">
              <Grid container spacing={2}>
                <Grid container item xs={12}>
                  <Grid item><h1>Edit Address</h1></Grid>
                </Grid>
                <AddressGroup />
              </Grid>
            </Form>
          </DialogContent>
          <DialogActions>
            <Button color="secondary" variant="outlined" onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button
              form="edit-address"
              color="primary" variant="contained"
              type="submit"
              disabled={isSubmitting || loading}
            >
              {mode === "Edit" ? "Save" : "Add"} Address
            </Button>
          </DialogActions>
        </>
      )}
    </Formik>
  </Modal></>
);

export const ProfileAddressesEditModal:React.FC<EditProps> = ({ address, isOpen, setIsOpen }) => {
  const [editAddressMutation, { loading }] = useMutation<EditUserProfileAddress>(editUserProfileAddressMutation, { refetchQueries: ["UserProfile"] });
  const { openSnackbar } = useSnackbar();

  const handleSubmit = (values:Address) => {
    // await validateStreetAddress(getAddress(values as NullableAddress))
    //   .then((response: ValidationResult) => {
    const addressValues = pick(values, ["id", "address1", "address2", "city", "state", "zip"]);
    editAddressMutation({
      variables: { address: addressValues,
        setAsCurrentAddress: false,
        setAsMailingAddress: false },
    }).then(() => openSnackbar(
      { message: "Address successfully edited.", status: "success" },
    ));

    setIsOpen(false);
    // });
  };

  return (<ProfileAddressesBaseModal
    mode="Edit"
    initialValues={address}
    isOpen={isOpen}
    setIsOpen={setIsOpen}
    onSubmit={handleSubmit}
    loading={loading}
  />
  );
};

export const ProfileAddressesAddModal:React.FC<AddProps> = ({ isOpen, setIsOpen }) => {
  const [addAddressMutation, { loading }] = useMutation<AddUserProfileAddress>(addUserProfileAddressMutation, { refetchQueries: ["UserProfile"] });
  const { openSnackbar } = useSnackbar();

  const initialValues:Address = {
    __typename: "UserProfileAddress",
    id: null,
    address1: null,
    address2: null,
    city: null,
    state: null,
    zip: null,
  };

  const handleSubmit = (values:Address) => {
    // await validateStreetAddress(getAddress(values as NullableAddress))
    //   .then((response: ValidationResult) => {
    const addressValues = pick(values, ["address1", "address2", "city", "state", "zip"]);

    addAddressMutation({
      variables: { address: addressValues,
        setAsCurrentAddress: false,
        setAsMailingAddress: false },
    }).then(() => openSnackbar(
      { message: "Address successfully added.", status: "success" },
    ));
    setIsOpen(false);
    // });
  };

  return (<ProfileAddressesBaseModal
    mode="Add"
    initialValues={initialValues}
    isOpen={isOpen}
    setIsOpen={setIsOpen}
    onSubmit={handleSubmit}
    loading={loading}
  />);
};
